import Button from "components/Button";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { currentBuildId, fetchLatestBuildId, isNewBuild } from "./version";

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const MIN_CHECK_GAP_MS = 60 * 1000;

const useNewBuildId = () => {
  const [latest, setLatest] = useState(null);
  const lastCheckRef = useRef(0);

  useEffect(() => {
    const current = currentBuildId();
    if (!current) return;

    const check = () => {
      if (Date.now() - lastCheckRef.current < MIN_CHECK_GAP_MS) return;
      lastCheckRef.current = Date.now();
      fetchLatestBuildId().then((id) => isNewBuild(current, id) && setLatest(id));
    };

    const onVisible = () => document.visibilityState === "visible" && check();
    const interval = setInterval(check, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  return latest;
};

const NewVersionBanner = () => {
  const { t } = useTranslation();
  const latest = useNewBuildId();
  const [dismissed, setDismissed] = useState(null);

  if (!latest || dismissed === latest) return null;

  return (
    <div
      role="status"
      className="fixed bottom-4 right-4 z-50 w-[calc(100%-2rem)] max-w-sm space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-lg print:hidden"
    >
      <div>
        <p className="text-sm font-semibold text-slate-800">{t("appUpdate.title")}</p>
        <p className="mt-1 text-sm text-slate-500">{t("appUpdate.description")}</p>
      </div>
      <div className="flex justify-end gap-3">
        <Button.Nd className="whitespace-nowrap" onClick={() => setDismissed(latest)}>
          {t("appUpdate.later")}
        </Button.Nd>
        <Button.Primary
          className="whitespace-nowrap"
          onClick={() => window.location.reload()}
        >
          {t("appUpdate.reload")}
        </Button.Primary>
      </div>
    </div>
  );
};

export default NewVersionBanner;
